import type { Me, RoleKey } from '@myrewards/contracts';
import type { QueryClient } from '@tanstack/react-query';
import { redirect, type LoaderFunctionArgs, type RouteObject } from 'react-router';
import { isApiError } from '../api';
import {
  budgetHistoryQuery,
  budgetsView,
  heatMapQuery,
  heatMapSearch,
  heatMapView,
  overviewQuery,
  overviewView,
  registerQuery,
  registerView,
  spendQuery,
  spendView,
  spotQuery,
  spotView,
  teamPeopleQuery,
  teamQuery,
  teamView,
} from '../api/insights';
import { noAccess } from '../app/access';
import type { RouteHandle } from '../app/RootLayout';
import { requireMe } from '../session/loaders';
import { skipRevalidationFor } from '../ui/revalidate';
import { loaderPaging } from '../ui/usePaging';
import { sectorLanding, sectorsOnly } from './labels';
import { peopleQueryOf, registerOf, teamListView } from './team-view';

/** "Not found" for the route's error boundary (shows the designed not-found page). */
function notFound(): Response {
  return new Response(null, { status: 404, statusText: 'Not Found' });
}

/**
 * Loads a report before it renders. Insights is only for people with at least one report (the
 * server decides Level 1); anyone else, and anyone opening a report they can't read (Finance on
 * the heat map), is told so (APP-134 §5): a report's address is no secret. A report the server
 * can't find stays not found.
 */
async function loadReport(
  queryClient: QueryClient,
  request: Request,
  load: (search: URLSearchParams) => Promise<unknown>,
): Promise<null> {
  const me = await requireMe(queryClient, request);
  // eslint-disable-next-line @typescript-eslint/only-throw-error -- React Router shows a thrown response as a route error.
  if (!me.nav.includes('insights')) throw noAccess(INSIGHTS_READERS);
  const search = new URL(request.url).searchParams;
  try {
    // The overview's library decides the tabs on every Insights page.
    await Promise.all([queryClient.query(overviewQuery()), load(search)]);
  } catch (error) {
    // eslint-disable-next-line @typescript-eslint/only-throw-error -- see above.
    if (isApiError(error) && error.status === 403) throw noAccess(null, error);
    // eslint-disable-next-line @typescript-eslint/only-throw-error -- see above.
    if (isApiError(error) && error.status === 404) throw notFound();
    throw error;
  }
  return null;
}

/**
 * A report paged on the server: once a new rows per page is saved, the size leaves the address
 * and the loader would read the same page again, so it doesn't run (APP-135 §7).
 */
const sizeSaved = skipRevalidationFor([], { sizeRemoved: ['size'] });
/** The Spot awards report comes whole and pages on the screen (APP-135 §4). */
const pagedOnScreen = skipRevalidationFor(['page', 'size']);

/** Who Insights is for, as No access names them (`levelOneFor`). */
const INSIGHTS_READERS = 'Executives, Finance, the Stock Admin team and Sector Leads';

/**
 * NAV1 (A47): this person's Insights reaches only their own sectors: a Sector Lead with no
 * company-wide role. The server's report scope says the same (`sectors`), and the tabs follow it.
 */
const COMPANY_WIDE: readonly RoleKey[] = ['executive', 'system_admin', 'finance', 'stock_admin'];
const ownSectorsOnly = (me: Me): boolean =>
  me.nav.includes('insights') && !me.roles.some((r) => COMPANY_WIDE.includes(r.role));

/** Who a report shows in Search & jump for: Insights readers, and the roles that open it. */
const reads =
  (...roles: readonly RoleKey[]) =>
  (me: Me): boolean =>
    me.nav.includes('insights') &&
    (roles.length === 0 || me.roles.some((r) => roles.includes(r.role)));

const handleOf = (
  pageLabel: string | ((me: Me) => string),
  topicIds: readonly string[],
  search: NonNullable<RouteHandle['search']>,
): RouteHandle => ({
  help:
    typeof pageLabel === 'string'
      ? { pageLabel, topicIds }
      : (me: Me) => ({ pageLabel: pageLabel(me), topicIds }),
  search,
});

/**
 * Insights (RPT1–RPT4, X6, F1): the overview and report library, All awards, the heat map,
 * Budgets & history, Spend and Spot awards. Each page loads its code on first use (APP-008) and
 * its data before it renders. Children of the signed-in layout.
 */
export function insightsRoutes(queryClient: QueryClient): RouteObject[] {
  return [
    {
      path: 'insights',
      loader: ({ request }: LoaderFunctionArgs) =>
        loadReport(queryClient, request, async (search) => {
          // NAV1 (A47): a reader of their own sectors only lands on Team recognition, as on the
          // board; their Insights has no overview tab.
          const base = await queryClient.query(overviewQuery());
          if (sectorsOnly(base.scope))
            // eslint-disable-next-line @typescript-eslint/only-throw-error -- React Router redirects by throwing a Response.
            throw redirect(sectorLanding(base.library));
          return queryClient.query(overviewQuery(overviewView(search)));
        }),
      lazy: async () => {
        const { OverviewPage } = await import('./OverviewPage');
        return { Component: OverviewPage };
      },
      handle: handleOf('Insights · Overview', ['pool', 'paid-from', 'in-progress', 'spot'], {
        label: 'Insights',
        meta: 'Budgets, recognition and every report',
        visible: (me) => reads()(me) && !ownSectorsOnly(me),
      }),
    },
    {
      path: 'insights/awards',
      loader: ({ request }: LoaderFunctionArgs) =>
        loadReport(queryClient, request, async (search) =>
          // The page and size the register will ask for, the person's saved size included.
          queryClient.query(
            registerQuery({
              ...registerView(search),
              ...(await loaderPaging(queryClient, search, 'award_register')),
            }),
          ),
        ),
      shouldRevalidate: sizeSaved,
      lazy: async () => {
        const { AwardsPage } = await import('./AwardsPage');
        return { Component: AwardsPage };
      },
      handle: handleOf(
        (me) => (ownSectorsOnly(me) ? 'Insights · Award register' : 'Insights · All awards'),
        ['paid-from', 'in-progress', 'award-record'],
        [
          {
            label: 'All awards',
            meta: 'Insights · every award, any time frame',
            visible: (me) => reads()(me) && !ownSectorsOnly(me),
          },
          {
            label: 'Award register',
            meta: 'Insights · every award for your sector, any time frame',
            visible: ownSectorsOnly,
          },
        ],
      ),
    },
    {
      path: 'insights/heat-map',
      loader: ({ request }: LoaderFunctionArgs) =>
        loadReport(queryClient, request, async (search) => {
          // A Sector Lead of one sector starts at their teams: a sector level would show one row
          // (APP-045 A31). A chosen level or sector in the address is kept.
          // The overview is cached for every Insights page; if it can't be read, keep the address.
          const scope = (await queryClient.query(overviewQuery()).catch(() => null))?.scope;
          if (
            scope?.kind === 'sectors' &&
            scope.sectors.length === 1 &&
            !search.has('level') &&
            !search.has('sectorId') &&
            search.get('direction') !== 'given'
          ) {
            const only = scope.sectors[0];
            if (only !== undefined)
              // eslint-disable-next-line @typescript-eslint/only-throw-error -- React Router redirects by throwing a Response.
              throw redirect(
                `/insights/heat-map${heatMapSearch({ ...heatMapView(search), level: 'team', sectorId: only.id })}`,
              );
          }
          return queryClient.query(
            heatMapQuery({
              ...heatMapView(search),
              ...(await loaderPaging(queryClient, search, 'heat_map')),
            }),
          );
        }),
      shouldRevalidate: sizeSaved,
      lazy: async () => {
        const { HeatMapPage } = await import('./HeatMapPage');
        return { Component: HeatMapPage };
      },
      handle: handleOf('Insights · Heat map', ['team-snapshot', 'spot'], {
        label: 'Recognition heat map',
        meta: 'Insights · who has been recognized, month by month',
        // APP-191 D15 A: the Stock Admin team too, company-wide.
        visible: reads('executive', 'stock_admin', 'system_admin', 'sector_lead'),
      }),
    },
    {
      path: 'insights/budgets',
      loader: ({ request }: LoaderFunctionArgs) =>
        loadReport(queryClient, request, (search) =>
          queryClient.query(budgetHistoryQuery(budgetsView(search))),
        ),
      lazy: async () => {
        const { BudgetsPage } = await import('./BudgetsPage');
        return { Component: BudgetsPage };
      },
      handle: handleOf('Insights · Budgets & history', ['pool', 'over-budget'], {
        label: 'Budgets & history',
        meta: 'Insights · allocated and awarded, year by year',
        visible: reads(),
      }),
    },
    {
      path: 'insights/spend',
      loader: ({ request }: LoaderFunctionArgs) =>
        loadReport(queryClient, request, (search) =>
          queryClient.query(spendQuery(spendView(search))),
        ),
      lazy: async () => {
        const { SpendPage } = await import('./SpendPage');
        return { Component: SpendPage };
      },
      handle: handleOf(
        (me) => (ownSectorsOnly(me) ? 'Insights · Budget & spend' : 'Insights · Spend'),
        ['pool', 'paid-from', 'in-progress'],
        [
          {
            label: 'Spend',
            meta: 'Insights · paid, in progress and left, by charge code',
            visible: (me) => reads()(me) && !ownSectorsOnly(me),
          },
          {
            label: 'Budget & spend',
            meta: 'Insights · your pool: budget, paid, in progress and left',
            visible: ownSectorsOnly,
          },
        ],
      ),
    },
    {
      path: 'insights/spot',
      loader: ({ request }: LoaderFunctionArgs) =>
        loadReport(queryClient, request, (search) =>
          queryClient.query(spotQuery(spotView(search))),
        ),
      shouldRevalidate: pagedOnScreen,
      lazy: async () => {
        const { SpotPage } = await import('./SpotPage');
        return { Component: SpotPage };
      },
      handle: handleOf('Insights · Spot awards', ['spot'], {
        label: 'Spot awards report',
        meta: 'Insights · Spot awards by sector, value and month',
        visible: reads('executive', 'stock_admin', 'system_admin'),
      }),
    },
    {
      // Team recognition (SL8): a Sector Lead's control view of their sector. Home's "Team
      // recognition" and "See who's not recognized yet" (`?filter=not-yet`) links land here.
      path: 'insights/team',
      loader: ({ request }: LoaderFunctionArgs) =>
        loadReport(queryClient, request, async (search) => {
          const view = teamView(search);
          const team = await queryClient.query(teamQuery(view));
          const list = {
            ...teamListView(search),
            ...(await loaderPaging(queryClient, search, 'team_people')),
          };
          await Promise.all([
            queryClient.query(teamPeopleQuery(peopleQueryOf(view, list, team.sector.id))),
            queryClient.query(registerQuery(registerOf(view, team.sector.id))),
          ]);
        }),
      shouldRevalidate: sizeSaved,
      lazy: async () => {
        const { TeamPage } = await import('./TeamPage');
        return { Component: TeamPage };
      },
      handle: handleOf('Insights · Team recognition', ['team-snapshot', 'award-record'], {
        label: 'Team recognition',
        meta: 'Insights · who on your teams has been recognized',
        visible: reads('sector_lead', 'executive', 'stock_admin', 'system_admin'),
      }),
    },
  ];
}
